<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useAuthStore } from '@/stores/auth.store'
import { useOrganisationStore } from '@/stores/organisation.store'
import { useIdeStore } from '@/stores/ide.store'
import settingService from '@/services/ide/settings/setting.service'
import shareService from '@/services/ide/settings/share.service'

import CopyShare from '@/components/ide/shared/CopyShareComp.vue'
import ideModal from '@/components/ide/ide/IdeModal.vue'
import Button from '@/components/atoms/button/index.vue'

const authStore = useAuthStore()
const organisationStore = useOrganisationStore()
const ideStore = useIdeStore()
const route = useRoute()

const isEmbedAllowed = ref<boolean>(false)
const httpErrorTimeOut = ref<NodeJS.Timeout | number | null>(null)
const httpError = ref<string>('')
const httpSuccessTimeOut = ref<NodeJS.Timeout | number | null>(null)
const httpSuccess = ref<string>('')
const loadUrlsInProgress = ref<boolean>(false)

const isProject = computed(() => {
  return ideStore.isProject && ideStore.isProjectId
})
const isAdvanced = computed(() => {
  return ideStore.isAdvanced
})
const isBlockly = computed(() => {
  return ideStore.isBlockly
})
const isHtml = computed(() => {
  return ideStore.isHtml
})
const isShowEmbed = computed(() => {
  return (
    isEmbedAllowed.value &&
    isProject.value &&
    !isAdvanced.value &&
    !isBlockly.value &&
    !isHtml.value
  )
})
const isShared = computed(() => {
  return ideStore.isProject.url || ideStore.isProject.embedUrl
})
const isProjectUrl = computed(() => {
  return ideStore.isProject.url || ''
})
const isEmbedUrl = computed(() => {
  return ideStore.isProject.embedUrl || ''
})
const pluginId = computed(() => {
  return ideStore.isProject.pluginId || ''
})
const isProBundlePlan = computed(() => {
  return ideStore.isProject.isProBundlePlan || false
})
/**
 * Get the share url
 */
const share = async () => {
  loadUrlsInProgress.value = true

  await shareService
    .editableShare()
    .then(() => {
      httpSuccess.value = 'Project Successfully Shared.'
    })
    .catch((error) => {
      httpError.value =
        error.response.data.message || 'Something went wrong. Please try again later.'
    })

  loadUrlsInProgress.value = false
}
/**
 * Get the share url
 */
const unshare = async () => {
  loadUrlsInProgress.value = true

  await shareService
    .editableUnshare()
    .then(() => {
      httpSuccess.value = 'Project Successfully Unshared.'
    })
    .catch((error) => {
      httpError.value =
        error.response.data.message || 'Something went wrong. Please try again later.'
    })

  loadUrlsInProgress.value = false
}
/**
 * Check embed permission
 */
const checkEmbedPermission = () => {
  if (organisationStore.isIndividualAccount) {
    isEmbedAllowed.value = true
  } else {
    if (organisationStore.isEmbedViewer) {
      isEmbedAllowed.value = false
    } else {
      if (
        organisationStore.isOrganisationOwner ||
        organisationStore.isOrganisationAdmin ||
        organisationStore.isEmbedAdmin
      ) {
        isEmbedAllowed.value = true
      } else {
        isEmbedAllowed.value = false
      }
    }
  }
}

/**
 * @description post await embed permissions
 * @param count number
 */
const postAwaitEmbedPermissions = async (count: number = 0) => {
  if (
    authStore.isInitiated &&
    authStore.isUserloggedIn &&
    organisationStore.instituteSubscriptionInfo &&
    organisationStore.activeAccountRoles
  ) {
    checkEmbedPermission()
  } else {
    if (count < 80) {
      setTimeout(() => {
        postAwaitEmbedPermissions(count + 1)
      }, 500)
    } else {
      isEmbedAllowed.value = false
    }
  }
}
onMounted(async () => {
  watch(route, () => {
    httpError.value = ''
    httpSuccess.value = ''
  })
  watch(httpError, () => {
    if (httpError.value) {
      if (httpErrorTimeOut.value) clearTimeout(httpErrorTimeOut.value)
      httpErrorTimeOut.value = setTimeout(() => {
        httpError.value = ''
      }, 8000)
    }
  })
  watch(httpSuccess, () => {
    if (httpSuccess.value) {
      if (httpSuccessTimeOut.value) clearTimeout(httpSuccessTimeOut.value)
      httpSuccessTimeOut.value = setTimeout(() => {
        httpSuccess.value = ''
      }, 5000)
    }
  })
  await postAwaitEmbedPermissions()
})
</script>

<template>
  <ideModal title="Editable Share" class="sm:mt-[8vh]">
    <div class="flex h-full flex-col gap-2">
      <p :class="['p-xsmall mb-2 px-4 text-center', 'error', , { hidden: !httpError }]">
        {{ httpError }}
      </p>
      <p :class="['p-xsmall mb-2 px-4 text-center', 'success', { hidden: !httpSuccess }]">
        {{ httpSuccess }}
      </p>
      <div class="h-full">
        <div v-if="!isShared" class="flex h-full flex-col gap-2">
          <p class="mb-8 mt-3 text-center text-sm sm:text-base">
            Do you want to share this project?
          </p>
          <div class="mx-auto flex w-full justify-center gap-3">
            <Button :disabled="loadUrlsInProgress" variant="filled" @click="share"> Share </Button>
            <Button @click="settingService.closePopup()"> Close </Button>
          </div>
        </div>
        <div v-else class="flex h-full flex-col gap-2">
          <CopyShare
            :isShowShared="true"
            :projectUrl="isProjectUrl"
            :showEmbed="isShowEmbed"
            :embedUrl="isEmbedUrl"
            :pluginId="pluginId"
            :isProBundlePlan="isProBundlePlan"
            :mini="true"
          />
          <div class="mx-auto mt-1 flex w-full justify-center gap-2">
            <Button :disabled="loadUrlsInProgress" @click="unshare" variant="filled">
              Unshare
            </Button>
            <Button @click="settingService.closePopup()"> Close </Button>
          </div>
        </div>
      </div>
    </div>
  </ideModal>
</template>
